import { useMemo } from "react";
import { DisplayUnit, LiveDataLookups } from "../models/toLiveDataRow";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { useDiagnosticsByIds } from "@/hooks/diagnostic/useDiagnosticsByIds";
import useDisplayMeasurementProfileDetail from "@/hooks/useDisplayMeasurementProfileDetail";
import { useUserDisplayProfile } from "@/hooks/users/useUserDisplayProfile";
import { parseGeotabId, sourceIdOf } from "@/utilities/parseGeotabId";

const NO_UNITS: NonNullable<ReturnType<typeof useDisplayMeasurementProfileDetail>["data"]> = [];
const EMPTY_DIAGNOSTICS = {} as NonNullable<ReturnType<typeof useDiagnosticsByIds>["data"]>;

/**
 * Everything a reading needs looked up before it can be shown: its diagnostic, the unit the user
 * asked for, and a readable source.
 *
 * Shared by every page that shows readings (Live-Daten, the fleet check, Motordaten), so the same
 * reading cannot end up described differently on two of them — and so the three lookups are built
 * once per set of diagnostics rather than once per reading.
 *
 * @param diagnosticIds - The diagnostics the readings refer to. Pass them sorted: the array is
 *   part of the diagnostics query key, and in arrival order every change refetches all of them.
 */
export function useLiveDataLookups(diagnosticIds: string[]): {
  lookups: LiveDataLookups;
  isLoading: boolean;
  isError: boolean;
} {
  const { me } = useGeotabClient();
  const { data: userDisplayProfile } = useUserDisplayProfile(me?.id);
  const { data: diagnosticUnits = NO_UNITS } = useDisplayMeasurementProfileDetail(
    userDisplayProfile?.profileId ?? undefined,
  );

  const {
    data: diagnosticsData,
    isLoading,
    isError = false,
  } = useDiagnosticsByIds(diagnosticIds, diagnosticIds.length === 0);
  const diagnostics = diagnosticsData ?? EMPTY_DIAGNOSTICS;

  const lookups = useMemo<LiveDataLookups>(
    () => ({
      diagnostics,
      units: unitsByDiagnosticId(diagnosticUnits),
      sources: sourcesByDiagnosticId(diagnostics),
    }),
    [diagnostics, diagnosticUnits],
  );

  return { lookups, isLoading, isError };
}

function unitsByDiagnosticId(
  details: { diagnosticId: string; displayUnitOfMeasure?: DisplayUnit | null }[],
): Record<string, DisplayUnit> {
  const units: Record<string, DisplayUnit> = {};

  for (const detail of details) {
    const unit = detail.displayUnitOfMeasure;
    units[detail.diagnosticId] = {
      name: unit?.name ?? "",
      factorFromBase: unit?.factorFromBase,
      offsetFromBase: unit?.offsetFromBase,
    };
  }

  return units;
}

function sourcesByDiagnosticId(
  diagnostics: Record<string, { source?: unknown }>,
): Record<string, string> {
  const sources: Record<string, string> = {};

  for (const [diagnosticId, diagnostic] of Object.entries(diagnostics)) {
    sources[diagnosticId] = parseGeotabId(sourceIdOf(diagnostic.source), "Source");
  }

  return sources;
}
